import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ILumoOneAppState } from '../../boot/configureStore';
import { applicationPageActions } from './applicationPageActions';
import LocationInput from '../../Components/LocationInput';
import { LocationData } from '../../LocationDataProvider/LocationData';
import {
	ApartmentSearchCriteria,
	LocationCriteria,
	ApartmentType,
	addLocationsToCriteria,
	removeLocationFromCriteria,
} from '../../Apartments/Browser/ApartmentSearchCriteria';
import { LocationSuggestion } from '../../Components/LocationSuggestion';
import { LocationTag } from '../../Components/LocationTag';
import { ResourceText, IUseResourceTexts, withResourceTextSupport } from '../../Components/ResourceText';
import Slider from 'rc-slider';
import { useFlagsState, FlagsState } from '../../Components/FlagsState';
import { useForm } from 'react-hook-form';
import { ValidationErrorDisplay } from './ValidationErrorDisplay';
import { ApplicationSubmitState } from './applicationSubmitState';
import LumoDatepicker from '../../Components/Datepicker';
import {
	ApplicationAM,
	ApplicantAM,
	ApplicantRole,
	ApplicantType,
	ApplicationApartmentType,
	ApplicationSearchArea,
} from '../Api/ApplicationAM';
import { isValidEmail, isValidPhoneNumber } from '../../InputValidators';
import { layoutActions } from '../../Common/Layout/layoutActions';
import { routeFromRoot, routes } from '../../Common/routes';
import { RangeSlider } from '../../Components/RangeSlider';
import { WaitingButton } from '../../Components/WaitingButton';
import SmartLink from '../../Components/SmartLink';
import { addTelemetryEventForCompanyApplicationCompletion } from '../Telemetry';
import { getValidationClassForInput, scrollToFirstInvalidField } from '../../Common/ValidationHelper';
import { pageViewTelemetryActions } from '../../Telemetry/pageViewTelemetryActions';
import PageMeta from '../../Common/Layout/PageMeta';
import { ApplicationSubmitErrorType } from './applicationPageState';
import { ApartmentTypeToggles } from '../../Apartments/Browser/ApartmentTypeToggles';
import { SliderValue, SliderValues } from '../../Components/SliderValues';
import { Language } from '../../Common/interfaces/language';
import { useNavigate } from 'react-router-dom';
import { useRunOnce } from '../../SSR/inlineRenderDispatchHelper';
import { getLocationTagValues } from '../../Components/Search/FilterLocationTags';

interface ILocalState {
	criteria: ApartmentSearchCriteria;
	submitClicked: boolean;
	validationFailed: boolean;
}

const defaultAreaMin = 30;
const defaultAreaMax = 70;
const defaultRentMax = 1000;
const initialState: ILocalState = {
	criteria: { areaInSqm: { min: defaultAreaMin, max: defaultAreaMax }, maxRent: defaultRentMax },
	submitClicked: false,
	validationFailed: false,
};

function CompanyApplicationPage(props: ICompanyApplicationPageProps) {
	const {
		register,
		formState: { errors, touchedFields },
		setValue,
		getValues,
		trigger,
	} = useForm({ mode: 'onChange' });
	const navigate = useNavigate();

	const [state, setState] = useState<ILocalState>(initialState);

	useRunOnce(() =>
		props.setLayoutValues({
			breadcrumbCurrentName: props.getResourceText('CompanyApplication_PageTitle'),
			breadcrumbParents: [],
			translateUrl: routeFromRoot(routes.companyApplication),
		}),
	);

	useEffect(() => {
		props.pageComplete({});
	}, []);

	useEffect(() => {
		if (state.validationFailed) {
			scrollToFirstInvalidField(errors, 'apartmentNeedInfo');
			setState({ ...state, validationFailed: false });
		}
	}, [state.validationFailed]);

	const apartmentTypeFlags = useFlagsState<ApartmentType>(state.criteria.roomCount ? state.criteria.roomCount : []);

	useEffect(() => {
		if (props.submitState === ApplicationSubmitState.Submitted) {
			addTelemetryEventForCompanyApplicationCompletion();

			props.resetApplication();
			navigate(routeFromRoot(routes.companyApplicationSent)[props.language]);
		}
	}, [props.submitState]);

	const handleLocationSelected = (selection: LocationSuggestion[]) => {
		const newLocationCriteria = addLocationsToCriteria(
			state.criteria.location,
			selection.filter((s) => typeof s !== 'string'),
		);
		setState({ ...state, criteria: { ...state.criteria, location: newLocationCriteria } });
	};

	const onLocationRemoved = (l: LocationSuggestion) => {
		const location = removeLocationFromCriteria(state.criteria.location!, l);
		setState({ ...state, criteria: { ...state.criteria, location: location } });
	};

	let locationTags: React.JSX.Element[] = [];
	let locationNames: string[] = [];
	const criteria = state.criteria;

	if (criteria.location) {
		const locationTagValues = getLocationTagValues(props.locationData!.businessEntities, criteria.location);

		locationTags = locationTagValues.map((l) => (
			<LocationTag
				locationData={props.locationData!}
				key={typeof l === 'string' ? l : l.type === 'zipCode' || l.type === 'additionalArea' ? l.name : l.id}
				value={l}
				onRemoved={onLocationRemoved}
			/>
		));
		locationNames = locationTagValues.map((l) => (typeof l === 'string' ? l : l.name));
	}

	async function onSubmitClicked(e: React.MouseEvent) {
		e.preventDefault();
		setState({ ...state, submitClicked: true });
		const contactInfoIsValid = await trigger();

		if (contactInfoIsValid && isLocationCriteriaSet(state.criteria.location)) {
			const application = getApplicationData(
				getValues(),
				state.criteria,
				apartmentTypeFlags,
				props.locationData!,
			);

			props.submitApplication(application);
		} else {
			setState({ ...state, validationFailed: true, submitClicked: true });
		}
	}

	function GetInputFieldClassName(fieldName: string, baseCssClassPostFix = '') {
		return getValidationClassForInput(fieldName, touchedFields, errors, baseCssClassPostFix);
	}

	const setMaxRent = (v: number) => setState({ ...state, criteria: { ...state.criteria, maxRent: v } });
	const setArea = ({ min, max }: { min: number; max: number }) => {
		setState({
			...state,
			criteria: { ...state.criteria, areaInSqm: { min, max } },
		});
	};
	const area = {
		min: typeof state.criteria.areaInSqm!.min === 'undefined' ? defaultAreaMin : state.criteria.areaInSqm!.min,
		max: typeof state.criteria.areaInSqm!.max === 'undefined' ? defaultAreaMax : state.criteria.areaInSqm!.max,
	};

	return (
		<>
			<PageMeta title={props.getResourceText('CompanyApplication_PageTitle')} />
			<div className="flow-companies">
				<div className="mod-heroCard">
					<picture className="mod-heroCard__image">
						<source media="(max-width: 539px)" srcSet="/images/yrityksille-mobi.jpg" />
						<source media="(max-width: 899px)" srcSet="/images/yrityksille-tablet.jpg" />
						<img className="mod-heroCard__image-img" src="/images/yrityksille-desktop.jpg" alt="" />
					</picture>
					<div className="mod-heroCard__wrapper">
						<div className="mod-heroCard__card atom-card">
							<div className="mod-heroCard__headingWrapper">
								<h1 className="mod-heroCard__heading">
									<ResourceText resourceKey="CompanyApplication_MainHeading" />
								</h1>
							</div>
							<div className="mod-heroCard__contentWrapper">
								<div className="mod-heroCard__ingress">
									<ResourceText resourceKey="CompanyApplication_Ingress" />
								</div>
								<div className="atom-card__text">
									<ResourceText resourceKey="CompanyApplication_PageInfo" textType="Markdown" />
									<SmartLink
										to={props.getResourceText(
											'CompanyApplication_WantToRentBusinessPremises_LinkUrl',
										)}
										className="plainLink"
									>
										<ResourceText resourceKey="CompanyApplication_WantToRentBusinessPremises_Link" />
										&nbsp;›
									</SmartLink>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="atom-card">
					<div className="atom-card__headingWrapper">
						<h2 className="atom-card__subheading">
							<strong>
								<ResourceText resourceKey="CompanyApplication_Heading_CompanyInfo" />
							</strong>
						</h2>
					</div>
					<div className="atom-card__contentWrapper">
						<div className={GetInputFieldClassName('companyName')}>
							<label className="input-basic__label" htmlFor="comp_name">
								<ResourceText resourceKey="CompanyApplication_Field_CompanyName" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_name"
								placeholder=""
								maxLength={200}
								{...register('companyName', { required: true })}
							/>
							<ValidationErrorDisplay fieldName="companyName" errors={errors} />
						</div>
						<div className={GetInputFieldClassName('businessId')}>
							<label className="input-basic__label" htmlFor="comp_number">
								<ResourceText resourceKey="CompanyApplication_Field_CompanyId" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_number"
								{...register('businessId', { required: true, validate: validateCompanyId })}
							/>
							<ValidationErrorDisplay fieldName="businessId" type="required" errors={errors} />
							<ValidationErrorDisplay
								fieldName="businessId"
								type="validate"
								errors={errors}
								message={props.getResourceText('CompanyApplication_ValidationError_CompanyId')}
							/>
						</div>
					</div>
				</div>
				<div className="atom-card">
					<div className="atom-card__headingWrapper">
						<h2 className="atom-card__subheading">
							<strong>
								<ResourceText resourceKey="CompanyApplication_Heading_ContactPerson" />
							</strong>
						</h2>
					</div>
					<div className="atom-card__contentWrapper">
						<div className={GetInputFieldClassName('contactFirstName')}>
							<label className="input-basic__label" htmlFor="comp_person_firstname">
								<ResourceText resourceKey="CompanyApplication_Field_FirstName" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_person_firstname"
								maxLength={200}
								{...register('contactFirstName', { required: true })}
							/>
							<ValidationErrorDisplay fieldName="contactFirstName" errors={errors} />
						</div>
						<div className={GetInputFieldClassName('contactLastName')}>
							<label className="input-basic__label" htmlFor="comp_person_lastname">
								<ResourceText resourceKey="CompanyApplication_Field_LastName" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_person_lastname"
								maxLength={200}
								{...register('contactLastName', { required: true })}
							/>
							<ValidationErrorDisplay fieldName="contactLastName" errors={errors} />
						</div>
						<div className={GetInputFieldClassName('contactPhone')}>
							<label className="input-basic__label" htmlFor="comp_person_phone">
								<ResourceText resourceKey="CompanyApplication_Field_Phone" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_person_phone"
								maxLength={50}
								{...register('contactPhone', { required: true, validate: isValidPhoneNumber })}
							/>
							<ValidationErrorDisplay fieldName="contactPhone" type="required" errors={errors} />
							<ValidationErrorDisplay
								fieldName="contactPhone"
								type="validate"
								message={props.getResourceText('CompanyApplication_ValidationError_CheckValue')}
								errors={errors}
							/>
						</div>
						<div className={GetInputFieldClassName('contactEmail')}>
							<label className="input-basic__label" htmlFor="comp_person_email">
								<ResourceText resourceKey="CompanyApplication_Field_Email" />{' '}
								<span className="input-basic__required">*</span>
							</label>
							<input
								className="input-basic__input"
								id="comp_person_email"
								type="email"
								maxLength={150}
								{...register('contactEmail', { required: true, validate: isValidEmail })}
							/>
							<ValidationErrorDisplay fieldName="contactEmail" type="required" errors={errors} />
							<ValidationErrorDisplay
								fieldName="contactEmail"
								type="validate"
								message={props.getResourceText('CompanyApplication_ValidationError_CheckValue')}
								errors={errors}
							/>
						</div>
					</div>
				</div>
				<div className="atom-card" id="apartmentNeedInfo">
					<div className="atom-card__headingWrapper">
						<h2 className="atom-card__subheading">
							<strong>
								<ResourceText resourceKey="CompanyApplication_Heading_ApartmentNeed" />
							</strong>
						</h2>
					</div>
					<div className="atom-card__contentWrapper">
						<div className="search-filters">
							<form className="search-filters__form">
								<fieldset className="search-filters__fieldset search-filters__fieldset--location">
									<div className="search-input__input-wrapper">
										<LocationInput
											locationData={props.locationData!}
											className="search-input__input"
											onLocationSelected={handleLocationSelected}
										/>
										<button className="search-input__button-activate" type="button">
											<span className="search-input__button-activate-label">
												<ResourceText
													resourceKey="ApartmentSearch_AddLocationButtonLabel"
													defaultText="Lisää"
												/>
											</span>
										</button>
										{state.submitClicked && !isLocationCriteriaSet(state.criteria.location) && (
											<div className="input-basic__error">
												<ResourceText resourceKey="CompanyApplication_SelectLocation" />
											</div>
										)}
									</div>
									<ul className="search-filters__locations">{locationTags}</ul>
									<span className="l-hidden" aria-live="polite">
										{locationNames.length
											? `${props.getResourceText('CompanyApplication_LocationFilters_AriaLive')} ${locationNames.join(', ')}`
											: null}
									</span>
								</fieldset>
								<div className="search-filters__main-filters search-filters__main-filters--grey-bg">
									<fieldset className="search-filters__fieldset search-filters__fieldset--rent">
										<legend className="search-filters__fieldset-legend -normal">
											<ResourceText resourceKey="CompanyApplication_MaximumRent" />
										</legend>
										<div className="range--max">
											<span aria-hidden={true}>
												<Slider
													value={state.criteria.maxRent!}
													onChange={setMaxRent}
													max={2300}
													step={10}
												/>
											</span>
											<SliderValues>
												<SliderValue value={0} unit="€" />
												<SliderValue
													value={state.criteria.maxRent!}
													unit="€"
													onChange={(e) => setMaxRent(parseInt(e.target.value))}
													aria-label={props.getResourceText(
														'CompanyApplication_MaximumRent_Label',
													)}
												/>
											</SliderValues>
										</div>
									</fieldset>
									<fieldset className="search-filters__fieldset search-filters__fieldset--apartment-type">
										<legend className="search-filters__fieldset-legend -normal">
											<ResourceText resourceKey="CompanyApplication_ApartmentType" />
										</legend>
										<ApartmentTypeToggles
											getValue={(type) => apartmentTypeFlags.isSet(type)}
											valueChanged={(type, val) => apartmentTypeFlags.set(type, val)}
										/>
									</fieldset>
									<fieldset className="search-filters__fieldset search-filters__fieldset--apartment-size">
										<legend className="search-filters__fieldset-legend -normal">
											<ResourceText resourceKey="CompanyApplication_ApartmentSize" />
										</legend>
										<div className="range--min range--max">
											<RangeSlider
												onChange={setArea}
												values={[area.min, area.max]}
												rangeMin={0}
												rangeMax={200}
												step={5}
											/>
											<SliderValues>
												<SliderValue
													value={area.min}
													unit="m2"
													onChange={(e) =>
														setArea({ min: parseInt(e.target.value), max: area.max })
													}
													aria-label={props.getResourceText(
														'CompanyApplication_ApartmentSize_Min',
													)}
												/>
												<SliderValue
													value={area.max}
													unit="m2"
													onChange={(e) =>
														setArea({ min: area.min, max: parseInt(e.target.value) })
													}
													aria-label={props.getResourceText(
														'CompanyApplication_ApartmentSize_Max',
													)}
												/>
											</SliderValues>
										</div>
									</fieldset>
									<fieldset className="search-filters__fieldset search-filters__fieldset--start-date">
										<legend className="search-filters__fieldset-legend -normal">
											<ResourceText resourceKey="CompanyApplication_ApartmentNeedDate" />
										</legend>
										<div className="input-date">
											<LumoDatepicker
												minDate={new Date(Date.now())}
												onSetDate={(value) => {
													setValue('apartmentNeededFrom', value);
												}}
											/>
											<input type="hidden" {...register('apartmentNeededFrom')} />
											<button type="button" className="input-date__button-activate">
												<span className="input-date__button-activate-label">
													<ResourceText resourceKey="CompanyApplication_Button_PickDate" />
												</span>
											</button>
										</div>
									</fieldset>

									<fieldset className="search-filters__fieldset">
										<legend className="search-filters__fieldset-legend -normal">
											<ResourceText resourceKey="CompanyApplication_Field_AdditionalInfo" />
										</legend>
										<textarea
											className="input-textarea"
											rows={5}
											maxLength={4000}
											placeholder={props.getResourceText(
												'CompanyApplication_Field_AdditionalInfo_Placeholder',
											)}
											{...register('additionalInformation')}
										></textarea>
									</fieldset>
								</div>
							</form>
						</div>
					</div>
				</div>
				<div className="atom-centerContent flow-companies__cta">
					{props.submitState === ApplicationSubmitState.Error && (
						<div style={{ marginBottom: 10 }}>
							<p className="atom-infoBox">
								<ResourceText resourceKey={getSubmitErrorTextResourceKey(props.submitErrorType)} />
							</p>
						</div>
					)}

					<WaitingButton
						waiting={props.submitState === ApplicationSubmitState.Submitting}
						className="atom-card__button atom-button atom-button--big"
						onClick={onSubmitClicked}
					>
						<ResourceText resourceKey="CompanyApplication_Button_SubmitApplication" />
					</WaitingButton>
				</div>
			</div>
		</>
	);
}

const apartmentTypeMap: Record<ApartmentType, ApplicationApartmentType> = {
	Nelio: ApplicationApartmentType.FourRoom,
	Kaksio: ApplicationApartmentType.TwoRoom,
	Kolmio: ApplicationApartmentType.ThreeRoom,
	SuurempiKuinNelio: ApplicationApartmentType.FiveOrMore,
	Yksio: ApplicationApartmentType.OneRoom,
};

function getApplicationData(
	data: any,
	criteria: ApartmentSearchCriteria,
	apartmentTypeFlags: FlagsState<ApartmentType>,
	locationData: LocationData,
): ApplicationAM {
	const applicants = [getContactPersonData(data)];

	return {
		additionalInformation: data.additionalInformation,
		allowOfferingsFromNearbyArea: true,
		allowOfferingsFromSameCity: true,
		apartmentNeededFrom: new Date(data.apartmentNeededFrom),
		applicants: applicants,
		apartmentMaxSize: criteria.areaInSqm!.max,
		apartmentMinSize: criteria.areaInSqm!.min,
		applicantIsPregnant: false,
		applicantType: ApplicantType.Company,
		currentResidence: undefined,
		financialSituation: undefined,
		language: 'fi',
		numberOfOtherResidents: 0,
		otherApplicantsCombinedIncome: 0,
		reasonsForApartmentNeed: [],
		searchConditions: {
			maxRent: criteria.maxRent!,
			apartmentTypes: apartmentTypeFlags.getAll().map((r) => apartmentTypeMap[r]),
			buildingTypes: [],
			features: [],
		},
		searchAreas: locationCriteriaToSearchAreas(criteria.location!, locationData),
		targets: [],
	};
}

function getContactPersonData(data: any): ApplicantAM {
	return {
		role: ApplicantRole.CompanyContactPerson,
		businessId: data.businessId,
		companyName: data.companyName,
		email: data.contactEmail,
		firstName: data.contactFirstName,
		lastName: data.contactLastName,
		phoneNumber: data.contactPhone,
		employer: '',
		identificationNumber: '',
		monthlyIncome: 0,
		postOffice: '',
		profession: '',
		streetAddress: '',
		zipCode: '',
	};
}

function locationCriteriaToSearchAreas(
	location: LocationCriteria,
	locationData: LocationData,
): ApplicationSearchArea[] {
	let businessEntities: ApplicationSearchArea[] = [];
	let municipalities: ApplicationSearchArea[] = [];
	let municipalitySubAreas: ApplicationSearchArea[] = [];
	let zipCodes: ApplicationSearchArea[] = [];

	if (location.businessEntityIds) {
		businessEntities = location.businessEntityIds.map((id) => ({
			street: locationData.businessEntities.find((be) => be.id === id)!.name,
			city: locationData.municipalities.find(
				(mun) => mun.id === locationData.businessEntities.find((be) => be.id === id)!.municipalityId,
			)!.name,
		}));
	}

	if (location.municipalities) {
		municipalities = location.municipalities.map((m) => ({ city: m.name }));
	}

	if (location.municipalitySubAreas) {
		municipalitySubAreas = location.municipalitySubAreas.map((m) => ({
			district: m.name,
			city: locationData.municipalities.find((mun) => mun.id === m.municipalityId)!.name,
		}));
	}

	if (location.zipCodes) {
		zipCodes = location.zipCodes.map((m) => ({ zipCode: m }));
	}

	return [...businessEntities, ...municipalities, ...municipalitySubAreas, ...zipCodes];
}

function isLocationCriteriaSet(location: LocationCriteria | undefined) {
	return (
		!!location &&
		(!arrayIsUndefinedOrEmpty(location.businessEntityIds) ||
			!arrayIsUndefinedOrEmpty(location.municipalities) ||
			!arrayIsUndefinedOrEmpty(location.municipalitySubAreas) ||
			!arrayIsUndefinedOrEmpty(location.zipCodes))
	);

	function arrayIsUndefinedOrEmpty<T>(array: T[] | undefined) {
		return !array || array.length === 0;
	}
}

const companyIdRegex = /^[0-9]{7}-[0-9]$/;

export function validateCompanyId(value: string): boolean {
	if (!companyIdRegex.test(value)) {
		return false;
	}

	const checkDigit = calculateCompanyIdCheckDigit(value);

	return checkDigit === value[8];
}

function calculateCompanyIdCheckDigit(value: string): string {
	const sum =
		parseInt(value[0]) * 7 +
		parseInt(value[1]) * 9 +
		parseInt(value[2]) * 10 +
		parseInt(value[3]) * 5 +
		parseInt(value[4]) * 8 +
		parseInt(value[5]) * 4 +
		parseInt(value[6]) * 2;

	const mod = sum % 11;

	if (mod === 0) {
		return '0';
	}
	if (mod === 1) {
		return '';
	}

	return (11 - mod).toString();
}

function getSubmitErrorTextResourceKey(errorType: ApplicationSubmitErrorType | undefined) {
	if (errorType === 'PreviousApplicationState') {
		return 'CompanyApplication_ApplicationSubmitFailed_PreviousApplication';
	}
	return 'CompanyApplication_ApplicationSubmitFailed';
}

export type ICompanyApplicationPageProps = ICompanyApplicationPageState &
	ICompanyApplicationPageDispatchProps &
	IUseResourceTexts;

export interface ICompanyApplicationPageState {
	locationData: LocationData | undefined;
	submitState: ApplicationSubmitState | undefined;
	submitErrorType: ApplicationSubmitErrorType | undefined;
	language: Language;
}

export interface ICompanyApplicationPageDispatchProps {
	submitApplication: typeof applicationPageActions.submitCompanyApplication.request;
	setLayoutValues: typeof layoutActions.setValues;
	resetApplication: typeof applicationPageActions.resetCompanyApplicationState;
	pageComplete: typeof pageViewTelemetryActions.pageComplete;
}

export default withResourceTextSupport(
	connect<
		ICompanyApplicationPageState,
		ICompanyApplicationPageDispatchProps,
		Record<string, never>,
		ILumoOneAppState
	>(
		({ locationData, applicationPage, language }) => ({
			locationData: locationData.locationData,
			submitState: applicationPage.companyApplicationSubmitState,
			submitErrorType: applicationPage.companyApplicationSubmitErrorType,
			language: language.currentLanguage,
		}),
		{
			submitApplication: applicationPageActions.submitCompanyApplication.request,
			setLayoutValues: layoutActions.setValues,
			resetApplication: applicationPageActions.resetCompanyApplicationState,
			pageComplete: pageViewTelemetryActions.pageComplete,
		},
	)(CompanyApplicationPage),
);
